import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { Trophy, User, LogOut, Plus, Menu, X, Shield, CreditCard, Crown, Users, Mic, Info, Flame } from "lucide-react";
import { useState } from "react";

// About Spardom Modal Component
const AboutSpardomModal = ({ trigger }) => (
  <Dialog>
    <DialogTrigger asChild>
      {trigger}
    </DialogTrigger>
    <DialogContent className="sm:max-w-md bg-zinc-900 border-white/10">
      <DialogHeader>
        <DialogTitle className="text-xl font-bold">About Spardom</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 text-muted-foreground">
        <p>
          <span className="text-foreground font-medium">Spardom</span> is a video-first debate arena where debate becomes performance.
        </p>
        <p>
          Comment sections don&apos;t reward skill — Spardom does.
        </p>
        <p>
          Here, debaters earn visibility by showing confidence, control, and presence on camera or mic.
        </p>
        <p>
          Each match plays out in three rounds: <span className="text-red-400">Hot Take</span>, <span className="text-blue-400">Clapback</span>, and <span className="text-yellow-400">Mic Drop</span>.
        </p>
        <p>
          The crowd vote decides the winner.
        </p>
        <p>
          <span className="text-foreground font-medium">Spar Rating</span> recognizes how you showed up — your presence and performance — so your reputation can grow over time.
        </p>
        <p className="text-foreground font-medium italic">
          Spardom means stardom earned by sparring.
        </p>
      </div>
    </DialogContent>
  </Dialog>
);

// New Spardom Logo using brand image (responsive sizing)
// Increased for better visibility - comparable to major platforms
// Mobile: 48px, Desktop: 52px
const SpardomLogo = () => (
  <img 
    src="/spardom-logo.png" 
    alt="Spardom" 
    className="h-12 md:h-[52px] w-auto object-contain"
  />
);

const Navbar = () => {
  const { user, logout, canDebate, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getSubscriptionBadge = () => {
    if (!user) return null;
    if (user.role === "admin") return <Badge variant="destructive" className="text-xs">Admin</Badge>;
    if (user.subscription_tier === "pro") return <Badge className="text-xs bg-accent text-accent-foreground"><Crown className="w-3 h-3 mr-1" />Pro</Badge>;
    if (user.subscription_tier === "access") return <Badge variant="secondary" className="text-xs">Access</Badge>;
    return <Badge variant="outline" className="text-xs">Free</Badge>;
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-white/10 pt-[env(safe-area-inset-top,0px)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo - prevent shrinking */}
          <Link to="/" className="flex items-center gap-2 group shrink-0 min-w-0 overflow-visible" data-testid="navbar-logo">
            <SpardomLogo />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/arena" 
              data-testid="nav-arena"
              className="text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1 font-medium"
            >
              <Flame className="w-4 h-4" />
              The Arena
            </Link>
            <Link 
              to="/browse" 
              data-testid="nav-browse"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Browse
            </Link>
            <Link 
              to="/groups" 
              data-testid="nav-groups"
              className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Users className="w-4 h-4" />
              Groups
            </Link>
            <Link 
              to="/leaderboard" 
              data-testid="nav-leaderboard"
              className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Trophy className="w-4 h-4" />
              Leaderboard
            </Link>
            <Link 
              to="/subscription" 
              data-testid="nav-subscription"
              className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <CreditCard className="w-4 h-4" />
              Pricing
            </Link>
            <AboutSpardomModal 
              trigger={
                <button 
                  data-testid="nav-about"
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                >
                  <Info className="w-4 h-4" />
                  About
                </button>
              }
            />
            
            {user ? (
              <>
                {canDebate && (
                  <Link to="/create">
                    <Button 
                      data-testid="nav-create"
                      size="sm" 
                      className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase btn-electric"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create
                    </Button>
                  </Link>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      data-testid="user-menu-trigger"
                      className={`p-0.5 rounded-full stage-glow-${user.stage_color || 'red'}`}
                    >
                      <Avatar className="w-9 h-9 border-2 border-white/20">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className={`${
                          user.stage_color === 'blue' ? 'bg-[#3b82f6]' :
                          user.stage_color === 'purple' ? 'bg-purple-500' :
                          user.stage_color === 'green' ? 'bg-green-500' :
                          user.stage_color === 'gold' ? 'bg-yellow-500' :
                          'bg-[#e11d48]'
                        }`}>
                          {user.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{user.username}</p>
                        {getSubscriptionBadge()}
                      </div>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={`/profile/${user.user_id}`} data-testid="menu-profile">
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-debates" data-testid="menu-my-debates">
                        <Trophy className="w-4 h-4 mr-2" />
                        My Debates
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/practice" data-testid="menu-practice">
                        <Mic className="w-4 h-4 mr-2" />
                        Practice Mode
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" data-testid="menu-subscription">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscription
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" data-testid="menu-admin">
                            <Shield className="w-4 h-4 mr-2" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    data-testid="nav-login"
                    variant="ghost" 
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    data-testid="nav-register"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                  >
                    Join with Code
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 py-4 space-y-4">
            <Link 
              to="/arena" 
              className="block text-orange-400 hover:text-orange-300 font-medium flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Flame className="w-4 h-4" />
              The Arena
            </Link>
            <Link 
              to="/browse" 
              className="block text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              Browse Debates
            </Link>
            <Link 
              to="/leaderboard" 
              className="block text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              Leaderboard
            </Link>
            <Link 
              to="/subscription" 
              className="block text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <AboutSpardomModal 
              trigger={
                <button 
                  className="block text-muted-foreground hover:text-foreground w-full text-left"
                >
                  About Spardom
                </button>
              }
            />
            
            {user ? (
              <>
                {canDebate && (
                  <Link 
                    to="/create" 
                    className="block text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Create Debate
                  </Link>
                )}
                <Link 
                  to={`/profile/${user.user_id}`}
                  className="block text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                <Link 
                  to="/my-debates"
                  className="block text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Debates
                </Link>
                <Link 
                  to="/practice"
                  className="block text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Practice Mode
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin"
                    className="block text-primary hover:text-primary/80"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Dashboard
                  </Link>
                )}
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block text-muted-foreground hover:text-foreground w-full text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="flex gap-3 pt-2">
                <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full">Sign In</Button>
                </Link>
                <Link to="/register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary text-primary-foreground">Join</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
